import React from 'react';
import ReactECharts from 'echarts-for-react';
import { Spinner, SpinnerSize } from '@fluentui/react';
import { DocumentTextRegular, MoneyRegular, PeopleRegular, ScalesRegular } from '@fluentui/react-icons';

import {
    AddendumComparisonDto,
    PatientTotalsDto,
    StatisticsSummaryDto
} from '../../../../models/Entities/Contracts/Statistics/ContractStatisticsModels';
import { fmt } from '../ContractStatistics.Functions';
import * as Functions from './AgreedVsBilled.Functions';
import { useEChartsTheme, useStableChartPalette } from '../EChartsTheme';

interface IProps {
    summary: StatisticsSummaryDto | null;
    rows: Functions.AgreedRow[];
    stageRows: Functions.AgreedStageRow[];
    totals: Functions.AgreedTotals;
    patients: PatientTotalsDto[];
    patientsTotal?: number;
    addendums: AddendumComparisonDto[];
    currency: string;
    isLoading: boolean;
    failed: boolean;
}

const STATUS_TAG: Record<Functions.AgreedStatus, { className: string; label: string }> = {
    complete: { className: 'fp-tag fp-tag--good', label: 'cobrado' },
    partial: { className: 'fp-tag fp-tag--warn', label: 'falta cobrar' },
    none: { className: 'fp-tag fp-tag--bad', label: 'sin cobrar' },
    over: { className: 'fp-tag fp-tag--warn', label: 'cobrado de más' }
};

/** Cuántos conceptos entran en el gráfico: más que esto no se leen. */
const MAX_BARS = 12;

/**
 * Lo pactado en el contrato, contra lo que realmente se cobró.
 *
 * El valor del contrato sale de la API de estadísticas y lo cobrado de los consumos.
 * Juntos contestan la pregunta que ninguna de las dos vistas contestaba sola:
 * ¿estoy cobrando lo que pacté?
 */
export const AgreedVsBilledComponent: React.FC<IProps> = ({
    summary,
    rows,
    stageRows,
    totals,
    patients,
    patientsTotal,
    addendums,
    currency,
    isLoading,
    failed
}) => {
    const money = (value: number) => fmt(value, currency);
    const chart = useStableChartPalette();
    const echartsTheme = useEChartsTheme();

    const chartRows = rows.slice(0, MAX_BARS);
    const chartOption = {
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            backgroundColor: chart.surface,
            borderColor: chart.axisLine,
            borderWidth: 1,
            textStyle: { color: chart.text, fontSize: 12 }
        },
        legend: { data: ['Pactado', 'Cobrado'], bottom: 0, textStyle: { fontSize: 11, color: chart.textSecondary } },
        grid: { left: 12, right: 24, top: 16, bottom: 40, containLabel: true },
        xAxis: { type: 'value', axisLabel: { fontSize: 10, color: chart.textSecondary }, splitLine: { lineStyle: { color: chart.gridLine } } },
        yAxis: {
            type: 'category',
            data: chartRows.map((row) => row.name).reverse(),
            axisTick: { show: false },
            axisLine: { lineStyle: { color: chart.axisLine } },
            axisLabel: { fontSize: 11, width: 160, overflow: 'truncate', color: chart.textSecondary }
        },
        series: [
            {
                name: 'Pactado',
                type: 'bar',
                barMaxWidth: 12,
                itemStyle: { color: chart.textSecondary, borderRadius: [0, 3, 3, 0] },
                data: chartRows.slice().reverse().map((row) => row.agreedAmount)
            },
            {
                name: 'Cobrado',
                type: 'bar',
                barMaxWidth: 12,
                itemStyle: { color: chart.series[0], borderRadius: [0, 3, 3, 0] },
                data: chartRows.slice().reverse().map((row) => row.billedAmount)
            }
        ]
    };

    if (isLoading) {
        return (
            <div className="cs-card">
                <div className="cs-loading cs-loading--compact">
                    <Spinner size={SpinnerSize.small} />
                    <span>Cargando lo pactado en el contrato…</span>
                </div>
            </div>
        );
    }

    if (failed) {
        return (
            <div className="cs-card">
                <div className="cs-cf-error" role="alert">
                    <strong>No se pudo obtener lo pactado en el contrato.</strong> El control de facturación de arriba
                    sigue siendo válido: usa los consumos, que se traen por otro lado.
                </div>
            </div>
        );
    }

    return (
        <>
            {/* ── Lo pactado ── */}
            <div className="cs-card">
                <div className="cs-card__header">
                    <div className="cs-card__title">Lo pactado contra lo cobrado</div>
                    <div className="cs-card__desc">
                        El valor del contrato frente a lo que efectivamente se cobró. Contesta si se está cobrando lo
                        que se pactó, concepto por concepto.
                    </div>
                </div>

                <div className="cs-kpi-grid">
                    <div className="cs-kpi-cell">
                        <div className="cs-kpi-cell__icon cs-kpi-cell__icon--gray"><DocumentTextRegular /></div>
                        <div className="cs-kpi-cell__value">{money(summary?.totalContractValue ?? totals.agreed)}</div>
                        <div className="cs-kpi-cell__label">Valor del contrato</div>
                        <div className="cs-kpi-cell__desc">Lo pactado según la configuración del contrato</div>
                    </div>
                    <div className="cs-kpi-cell">
                        <div className="cs-kpi-cell__icon cs-kpi-cell__icon--blue"><MoneyRegular /></div>
                        <div className="cs-kpi-cell__value">{money(totals.billed)}</div>
                        <div className="cs-kpi-cell__label">Cobrado</div>
                        <div className="cs-kpi-cell__desc">Suma de los consumos cargados en el período elegido</div>
                        {totals.coverage !== null && (
                            <div className="cs-kpi-cell__sub">{totals.coverage}% de lo pactado</div>
                        )}
                    </div>
                    <div className="cs-kpi-cell">
                        <div className="cs-kpi-cell__icon cs-kpi-cell__icon--orange"><ScalesRegular /></div>
                        <div className="cs-kpi-cell__value">
                            <span className={totals.difference < 0 ? 'fp-amount--negative' : 'fp-amount--positive'}>
                                {money(totals.difference)}
                            </span>
                        </div>
                        <div className="cs-kpi-cell__label">Diferencia</div>
                        <div className="cs-kpi-cell__desc">
                            {totals.difference < 0 ? 'Falta cobrar respecto de lo pactado' : 'Se cobró por encima de lo pactado'}
                        </div>
                        <div className="cs-kpi-cell__sub">
                            {totals.neverBilled} conceptos sin cobrar · {totals.unexpected} cobrados fuera del pactado
                        </div>
                    </div>
                    <div className="cs-kpi-cell">
                        <div className="cs-kpi-cell__icon cs-kpi-cell__icon--blue"><PeopleRegular /></div>
                        <div className="cs-kpi-cell__value">{money(summary?.avgPerPatient ?? 0)}</div>
                        <div className="cs-kpi-cell__label">Promedio por paciente</div>
                        <div className="cs-kpi-cell__desc">
                            Según el contrato, sobre {summary?.patientCount ?? 0} pacientes
                        </div>
                    </div>
                </div>

                {rows.length === 0 ? (
                    <div className="cs-empty cs-empty--compact">
                        <span>El contrato no devolvió conceptos valorizados para comparar.</span>
                    </div>
                ) : (
                    <>
                        <div className="cs-card__chart-body">
                            <ReactECharts
                                theme={echartsTheme}
                                option={chartOption}
                                style={{ height: Math.max(220, chartRows.length * 34) }}
                                opts={{ renderer: 'svg' }}
                            />
                        </div>

                        <div className="cs-card__body">
                            <div className="cs-table-wrap">
                                <table className="cs-table">
                                    <thead>
                                        <tr>
                                            <th>Concepto</th>
                                            <th className="cs-table__amount">Pactado</th>
                                            <th className="cs-table__amount">Visitas pactadas</th>
                                            <th className="cs-table__amount">Cobrado</th>
                                            <th className="cs-table__amount">Diferencia</th>
                                            <th className="cs-table__amount">Cobertura</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row) => (
                                            <tr key={row.key}>
                                                <td>
                                                    {row.name}
                                                    {row.isUnexpected && (
                                                        <> <span className="fp-tag fp-tag--bad" title="Se cobró, pero no figura entre los conceptos valorizados del contrato.">fuera del pactado</span></>
                                                    )}
                                                </td>
                                                <td className="cs-table__amount">{money(row.agreedAmount)}</td>
                                                <td className="cs-table__amount">{row.agreedVisits || '—'}</td>
                                                <td className="cs-table__amount">{money(row.billedAmount)}</td>
                                                <td className="cs-table__amount">
                                                    <span className={row.difference < 0 ? 'fp-amount--negative' : undefined}>
                                                        {money(row.difference)}
                                                    </span>
                                                </td>
                                                <td className="cs-table__amount">
                                                    <span className={STATUS_TAG[row.status].className}>
                                                        {row.coverage === null ? STATUS_TAG[row.status].label : `${row.coverage}%`}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                        <tr className="fp-total">
                                            <td>Total</td>
                                            <td className="cs-table__amount">{money(totals.agreed)}</td>
                                            <td className="cs-table__amount">—</td>
                                            <td className="cs-table__amount">{money(totals.billed)}</td>
                                            <td className="cs-table__amount">
                                                <span className={totals.difference < 0 ? 'fp-amount--negative' : undefined}>
                                                    {money(totals.difference)}
                                                </span>
                                            </td>
                                            <td className="cs-table__amount">
                                                {totals.coverage === null ? '—' : `${totals.coverage}%`}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* ── Por visita y por paciente ── */}
            <div className="cs-two-col">
                <div className="cs-card">
                    <div className="cs-card__header">
                        <div className="cs-card__title">Pactado y cobrado por visita</div>
                        <div className="cs-card__desc">
                            Primero las visitas donde más se aleja lo cobrado de lo pactado.
                        </div>
                    </div>
                    <div className="cs-card__body">
                        {stageRows.length === 0 ? (
                            <div className="cs-empty cs-empty--compact"><span>El contrato no trae desglose por visita.</span></div>
                        ) : (
                            <div className="cs-table-wrap">
                                <table className="cs-table">
                                    <thead>
                                        <tr>
                                            <th>Visita</th>
                                            <th className="cs-table__amount">Pactado</th>
                                            <th className="cs-table__amount">Cobrado</th>
                                            <th className="cs-table__amount">Diferencia</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {stageRows.map((row) => (
                                            <tr key={row.stageId}>
                                                <td>{row.name}</td>
                                                <td className="cs-table__amount">{money(row.agreedAmount)}</td>
                                                <td className="cs-table__amount">{money(row.billedAmount)}</td>
                                                <td className="cs-table__amount">
                                                    <span className={row.difference < 0 ? 'fp-amount--negative' : undefined}>
                                                        {money(row.difference)}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                <div className="cs-card">
                    <div className="cs-card__header">
                        <div className="cs-card__title">Pactado por paciente</div>
                        <div className="cs-card__desc">
                            Lo que el contrato valoriza para cada paciente, de mayor a menor.
                        </div>
                    </div>
                    <div className="cs-card__body">
                        {patients.length === 0 ? (
                            <div className="cs-empty cs-empty--compact"><span>No hay pacientes valorizados en el contrato.</span></div>
                        ) : (
                            <div className="cs-table-wrap">
                                <table className="cs-table">
                                    <thead>
                                        <tr>
                                            <th>Paciente</th>
                                            <th className="cs-table__amount">Pactado</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {patients.map((patient) => (
                                            <tr key={patient.patientId || patient.patientDisplayName}>
                                                <td>{patient.patientDisplayName || patient.patientId || '—'}</td>
                                                <td className="cs-table__amount">{money(patient.totalAmount)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {!!patientsTotal && patientsTotal > patients.length && (
                                    <div className="cs-kpi-cell__desc" style={{ padding: '8px 0' }}>
                                        Mostrando {patients.length} de {patientsTotal} pacientes.
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Adendas ── */}
            {addendums.length > 1 && (
                <div className="cs-card">
                    <div className="cs-card__header">
                        <div className="cs-card__title">Comparación por adenda</div>
                        <div className="cs-card__desc">Cuánto agregó cada adenda al valor del contrato.</div>
                    </div>
                    <div className="cs-card__body">
                        <div className="cs-table-wrap">
                            <table className="cs-table">
                                <thead>
                                    <tr>
                                        <th>Adenda</th>
                                        <th className="cs-table__amount">Valor pactado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {addendums.map((item) => (
                                        <tr key={item.contractAddendumId}>
                                            <td>{item.addendumName || `Adenda #${item.contractAddendumId}`}</td>
                                            <td className="cs-table__amount">{money(item.totalAmount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AgreedVsBilledComponent;
